// useStartButton.js - React hook for loading and initializing StartButton SDK
// Updated to match obana-shop implementation and StartButton docs
import { useRef } from 'react';

/**
 * @typedef {Object} StartButtonConfig
 * @property {string} phone
 * @property {string} key
 * @property {number} amount
 * @property {string[]} paymentMethods
 * @property {'test'|'prod'} [env]
 * @property {function} success
 * @property {function} close
 * @property {function} error
 * @property {string} currency
 * @property {string} email
 * @property {Object} [metadata]
 */

export const useStartButton = () => {
  const scriptLoaded = useRef(false);

  const loadScript = () => {
    return new Promise((resolve, reject) => {
      if (scriptLoaded.current) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.startbutton.tech/v2/sb-web-sdk.min.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        scriptLoaded.current = true;
        resolve();
      };
      script.onerror = (error) => {
        console.error('Failed to load StartButton script', error);
        reject(new Error('Failed to load StartButton script'));
      };
      document.head.appendChild(script);
    });
  };

  /**
   * @param {StartButtonConfig} config
   */
  const initializePayment = async (config) => {
    try {
      if (!scriptLoaded.current) {
        await loadScript();
      }
      if (window.SBInit) {
        window.SBInit(config);
      } else {
        throw new Error('StartButton SDK not available');
      }
    } catch (error) {
      console.error('Error initializing StartButton:', error);
      throw error;
    }
  };

  return { initializePayment };
};
